import { Badge, Button, Card, EmptyState, ErrorState, Spinner, Table, Td, Th } from '@/components/ui';
import { useConfirm } from '@/components/ConfirmProvider';
import { formatDateTime } from '@/lib/format';
import type { ActiveSession } from './types';
import { useSessions, useTerminateSession } from './useAdmin';

/**
 * 활성 세션 목록과 강제 로그아웃.
 *
 * 🔴 이 화면이 기기를 분실했을 때의 유일한 대응 수단이다.
 *   자동 로그인 쿠키가 30일짜리라 폰을 잃어버리면 한 달 동안 남이 열어볼 수 있다.
 *   비밀번호를 바꿔도 이미 발급된 세션은 살아 있다 — 세션은 비밀번호를 다시 묻지 않는다.
 *
 * JWT 를 쓰지 않기로 한 이유가 이것이다. JWT 였다면 만료까지 기다리거나
 * 블랙리스트를 따로 관리해야 한다. 서버 세션은 Redis 에서 키 하나 지우면 끝난다.
 * (00-architecture.md 1절)
 */
export function SessionsPanel() {
  const sessionsQuery = useSessions();
  const terminateMutation = useTerminateSession();
  const confirm = useConfirm();

  if (sessionsQuery.isLoading) {
    return <Spinner />;
  }
  if (sessionsQuery.error) {
    return (
      <ErrorState
        message={sessionsQuery.error.message}
        onRetry={() => void sessionsQuery.refetch()}
      />
    );
  }

  const sessions = sessionsQuery.data ?? [];

  /*
    끊기 전에 무엇이 일어나는지 알려준다.

    "종료" 라는 두 글자만으로는 무엇이 끝나는지 알 수 없다 —
    계정이 지워지는지, 비밀번호가 바뀌는지, 그 기기만 로그아웃되는지.
    특히 자기 세션을 끊으면 이 화면에서 즉시 튕겨 나가므로 미리 말해야 한다.
  */
  const terminate = async (session: ActiveSession) => {
    const ok = await confirm({
      title: session.current
        ? '지금 쓰는 기기를 로그아웃할까요?'
        : `${session.displayName} 님의 기기를 로그아웃할까요?`,
      description: (
        <>
          <p>그 기기의 로그인만 끊깁니다. 비밀번호는 그대로라 다시 로그인할 수 있습니다.</p>
          {session.current && (
            <p className="mt-2 text-danger">
              이 세션은 지금 보고 있는 화면입니다. 끊으면 곧바로 로그인 화면으로 나갑니다.
            </p>
          )}
        </>
      ),
      confirmText: '로그아웃',
      tone: 'danger',
    });

    if (ok) {
      terminateMutation.mutate(session.sessionId);
    }
  };

  return (
    <Card
      title="활성 세션"
      action={
        <Button variant="ghost" onClick={() => void sessionsQuery.refetch()}>
          새로고침
        </Button>
      }
    >
      {sessions.length === 0 ? (
        <EmptyState
          title="살아 있는 세션이 없습니다."
          description="지금 로그인해 있는 사람이 없다는 뜻입니다."
        />
      ) : (
        <Table>
          {/*
            열이 셋이다. 그전에는 넷이었는데 날짜 두 개를 한 칸에 두 줄로 합쳤다.
            폰에서 네 열은 각 칸이 좁아져 날짜가 두 줄로 접히고, 그러면
            행마다 높이가 달라져 표가 들쭉날쭉해진다.

            머리글은 전부 가운데 정렬이다. 값의 정렬과 다른 것이 의도다 —
            머리글은 "이 열이 무엇인가" 라는 이름표이지 값이 아니다.
          */}
          <thead>
            <tr>
              <Th>사용자</Th>
              <Th>로그인 / 만료예정</Th>
              <Th align="center">작업</Th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session) => (
              <tr key={session.sessionId}>
                <Td>
                  <span className="block font-medium text-fg">
                    {session.displayName}
                    {/* 자기 세션을 모르고 끊으면 즉시 로그아웃되어 당황한다 */}
                    {session.current && (
                      <span className="ml-1.5 align-middle">
                        <Badge tone="success">이 기기</Badge>
                      </span>
                    )}
                  </span>
                  {/* 닉네임이 같은 사람이 있을 수 있어 아이디를 아래에 작게 붙인다 */}
                  <span className="block text-xs text-fg-subtle">{session.loginId}</span>
                </Td>

                <Td numeric>
                  {/*
                    위가 로그인 시각, 아래가 만료 예정이다.

                    만료 예정은 접근할 때마다 뒤로 밀린다.
                    "지금 아무것도 안 하면 언제 끊기나" 로 읽어야 한다.
                    그래서 로그인 시각과 나란히 둔다 — 둘의 간격이
                    이 세션이 얼마나 오래 살아 있었는지를 말해준다.
                  */}
                  <span className="block text-xs text-fg">
                    {formatDateTime(session.createdAt)}
                  </span>
                  <span className="block text-xs text-fg-subtle">
                    {formatDateTime(session.expiresAt)}
                  </span>
                </Td>

                <Td align="center">
                  <Button
                    variant="danger"
                    disabled={terminateMutation.isPending}
                    onClick={() => void terminate(session)}
                  >
                    {/* whitespace-nowrap 이 없으면 좁은 칸에서 종/료 로 쪼개진다 */}
                    <span className="whitespace-nowrap">종료</span>
                  </Button>
                </Td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {terminateMutation.error && (
        <p className="mt-3 text-sm text-danger">{terminateMutation.error.message}</p>
      )}

      <p className="mt-4 text-xs text-fg-subtle">
        기기를 잃어버렸다면 사용자·권한 탭에서 그 사람을 열고 '모든 기기에서 로그아웃'을 누르세요.
        비밀번호는 그대로 둡니다(비밀번호를 바꿔도 그 사람의 세션은 모두 끊깁니다).
      </p>
    </Card>
  );
}
